import { isNil } from '@gfe/core/utils/shared-utils';

export type UserEntityRegistrations =
  | { type: 'Generic'; count: number }
  | { type: 'Nominal'; registration_uids: string[] };

export function renderRegistrations(reg: UserEntityRegistrations): string {
  if (reg.type === 'Generic') return `Generic (${reg.count})`;
  return reg.registration_uids.join(', ');
}

/**
 * One assignable registration slot. Mirrors the backend's strict `oneOf [{ code }, { copy }]`,
 * so exactly one key is ever set.
 */
export type RegistrationSlot = { copy: number } | { code: string };

/**
 * The slots a resource offers, in its own declared order. `copy` is 0-based, the convention
 * the job-importation picker already writes. An absent `registrations` — legal on trailers and
 * drivers — means the resource has no slots at all, which makes an unassigned registration the
 * correct state rather than a gap to fill.
 */
export function registrationSlots(
  registrations: UserEntityRegistrations | undefined
): RegistrationSlot[] {
  if (isNil(registrations)) return [];
  if (registrations.type === 'Generic') {
    return Array.from({ length: registrations.count }, (_, index) => ({ copy: index }));
  }
  return registrations.registration_uids.map((code) => ({ code }));
}

/** Stable identity of a slot, for matching an assignment against the offered set. */
export function registrationSlotKey(slot: { copy?: number; code?: string }): string {
  return isNil(slot.copy) ? `code:${slot.code}` : `copy:${slot.copy}`;
}
